<template>
  <div class="container-announcement">

    <div class="carousel carousel-slide carousel-fade" :class="{ 'carousel-initialized': initialized }">
      <div class="carousel-inner">
        <div
          v-for="(slide, idx) in slides"
          :key="idx"
          class="carousel-item"
          :class="{ active: idx === activeIndex }"
        >
          <div class="carousel-announcement">
            <h2>
              <span>{{ slide.line1 }}</span><br/>
              <span>{{ slide.line2 }}</span>
            </h2>
          </div>
          <div class="carousel-image" :class="slide.highlightClass"></div>
        </div>
      </div>

      <div class="carousel-headline">
        <div class="carousel-indicators">
          <button
            v-for="(slide, idx) in slides"
            :key="idx"
            type="button"
            :class="{ active: idx === activeIndex }"
            @click="goTo(idx)"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref} from 'vue';

interface Slide {
  line1: string;
  line2: string;
  highlightClass: string;
  interval: number;  // ms
}

const slides: Slide[] = [
  {line1: 'AI Product Management with',         line2: 'Contextual Intelligence',                highlightClass: 'highlight-1', interval: 3500},
  {line1: 'The Second Brain for product managers,', line2: 'agencies and software houses.',     highlightClass: 'highlight-2', interval: 3500},
  {line1: 'Convert client meeting into',         line2: 'requirements & estimation in minutes', highlightClass: 'highlight-3', interval: 3500},
];

const activeIndex = ref(0);
const initialized = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleNext(): void {
  clearTimer();
  const current = slides[activeIndex.value];
  if (!current) return;
  timer = setTimeout(() => {
    activeIndex.value = (activeIndex.value + 1) % slides.length;
    scheduleNext();
  }, current.interval);
}

function clearTimer(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function goTo(idx: number): void {
  activeIndex.value = idx;
  scheduleNext();
}

onMounted(() => {
  setTimeout(() => { initialized.value = true; }, 600);
  scheduleNext();
});

onBeforeUnmount(clearTimer);
</script>

<style lang="scss" scoped>


  .container-announcement{
    background: var(--dark-200);
    color: var(--light-100);
    width: 100%;
    height: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  .carousel{
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    position: relative;
    overflow: hidden;
  }

  .carousel-announcement{
    margin-right: 0;
    margin-left: 0;
    text-align: left;

    margin-bottom: 32px;
    position: absolute;
    z-index: 1;
    bottom: 60px;
    left: 60px;
    h2{
      font-size: var(--fs-32);
      font-weight: 600;
      color: var(--text-light-fixed);


      span{
        background: var(--accent-100);
        margin: var(--space-4);
        display: inline-block;
        padding: var(--space-8) var(--space-12);
        border-radius: $br-md;
        letter-spacing: -1.6px;
        &:last-child{
          margin-bottom: var(--space-0);
        }
      }
    }
    p{
      font-size: var(--fs-16);
      letter-spacing: 0.2px;
      color: var(--text-light-fixed);
    }
  }

  .carousel-inner{
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
  }

  .carousel-image{
    width: 100%;
    height: 100vh;
    display: block;
    min-height: 100%;
    position: absolute;
    top: 0;
    background-size: cover !important;
    background-repeat: no-repeat !important;
    background-position: center center !important;
  }

  .carousel-item{
    text-align: left;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.6s ease-in-out;
    pointer-events: none;
  }

  .carousel-fade .carousel-item.active {
    opacity: 1;
    pointer-events: auto;
  }

  .carousel-fade:not(.carousel-initialized) .carousel-item.active {
    animation-name: fadeInOpacity;
    animation-iteration-count: 1;
    animation-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
    animation-duration: 0.6s;
  }

  .carousel-fade.carousel-initialized .carousel-item.active {
    animation: none;
  }

  @keyframes fadeInOpacity {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .carousel-headline{
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: absolute;
    bottom: 40px;
    left: 60px;
    z-index: 2;
  }

  .carousel-indicators{
    display: flex;
    gap: 10px;
    margin: 0;
    padding: 0;

    button {
      width: 10px;
      height: 10px;
      border-radius: $br-circle;
      border: 2px solid var(--light-100);
      background-color: transparent;
      opacity: 1;
      padding: 0;
      cursor: pointer;
      transition: background-color 0.2s ease-in-out;
    }
    button.active {
      background-color: var(--light-100);
    }
  }

  .highlight-1{
    background: linear-gradient(to bottom, rgb(0 0 0 / 0%), rgb(0 0 0 / 50%)), url(@/assets/highlights/auth-bg-1.jpg);
  }
  .highlight-2{
    background: linear-gradient(to bottom, rgb(0 0 0 / 0%), rgb(0 0 0 / 50%)), url(@/assets/highlights/auth-bg-2.jpg);
  }

  .highlight-3{
    background: linear-gradient(to bottom, rgb(0 0 0 / 0%), rgb(0 0 0 / 50%)), url(@/assets/highlights/auth-bg-3.jpg);
  }
</style>
